import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  TextInput,
  Tooltip,
  Title,
  Modal,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import ChemicalStructureSVG, {ChemicalStructureImageData} from 'chemistry/ChemicalStructureSVG';
import {EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import EditChemicalCounterpartModal from 'chemistry/EditChemicalCounterpartModal';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {ScrollableTableColumn, ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';

export interface ChemicalCounterpartItem {
  id: string;
  name: string;
  code: string | null;
  calculatedMolecularWeight: number | null;
  canonicalSmiles: string;
  imageData: ChemicalStructureImageData;
  archiveData: {archived: boolean; purpose: string | null};
}

interface ChemicalCounterpartRowSortableData {
  name: string;
  code: string;
  molecularWeight: string | undefined;
}

interface ChemicalCounterpartRowData extends ChemicalCounterpartRowSortableData {
  id: string;
  chemicalStructure: React.ReactNode;
  canonicalSmiles: string;
  actions: JSX.Element;
}

interface ChemicalCounterpartRowExtra {
  calculatedMolecularWeight: number | null;
}

interface ChemicalCounterpartRow extends ScrollableTableRow {
  data: ChemicalCounterpartRowData;
  extra: ChemicalCounterpartRowExtra;
}

function ChemicalCounterpartActions({
  id,
  name,
  item,
  onArchive,
  onUnarchive,
  onEditClick,
  canEdit,
  canArchive,
}: {
  id: string;
  name: string;
  item: ChemicalCounterpartItem;
  onArchive: (itemIds: Array<string>, purpose: string, options?: ArchiveOptionsForModal) => Promise<void>;
  onUnarchive: (itemIds: Array<string>) => Promise<void>;
  onEditClick: () => void;
  canEdit: boolean;
  canArchive: boolean;
}): JSX.Element {
  return (
    <Group>
      {canArchive && (
        <ArchiveOrUnarchiveButton
          item={item}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          isArchivable={true}
          isUnarchivable={true}
        />
      )}
      {!item.archiveData.archived && (
        <Tooltip label="Edit" withinPortal={true}>
          <ActionIcon aria-label="Edit" disabled={!canEdit} variant="tertiary" onClick={onEditClick}>
            <BenchlingIcon icon="edit" />
          </ActionIcon>
        </Tooltip>
      )}
      <Tooltip label={EXPORT_AUDIT_LOG_DISPLAY_STRING} withinPortal={true}>
        <ActionIcon
          aria-label={`${EXPORT_AUDIT_LOG_DISPLAY_STRING} for ${name}`}
          variant="tertiary"
          onClick={() => {
            exportAuditLogs([id]);
          }}
        >
          <BenchlingIcon icon="audit-trail" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

interface CreateChemicalCounterpartModalProps {
  counterpartName: string;
  onClose: () => void;
  onAddNew: (name: string, code: string, sketcherValue: string) => Promise<void>;
}

function CreateChemicalCounterpartModal({
  counterpartName,
  onClose,
  onAddNew,
}: CreateChemicalCounterpartModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [sketcherValue, setSketcherValue] = useState<ChemicalStructureDrawerValue>(null);

  const handleMoleculeChange = useCallback((value: ChemicalStructureDrawerValue) => {
    setSketcherValue(value);
  }, []);

  return (
    <Modal
      title={`Create new ${counterpartName}`}
      opened={true}
      onClose={onClose}
      size="xl"
      styles={{
        content: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Flex direction="column" h="100%" style={{flex: 1}}>
        <Flex direction="column" style={{minHeight: 350, flex: 1}}>
          <ChemicalStructureDrawer
            mode={ChemicalStructureDrawerMode.STANDARD}
            onMoleculeChange={handleMoleculeChange}
            initialValue={EMPTY_MOLFILE}
            outputFormat={MarvinChemicalFormat.SMILES}
          />
        </Flex>
        <SimpleGrid cols={2}>
          <TextInput
            autoFocus={true}
            label="Name"
            required={true}
            value={name}
            placeholder="Name"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <TextInput
            label="Code"
            value={code}
            placeholder="Code"
            onChange={(e) => {
              setCode(e.target.value);
            }}
          />
        </SimpleGrid>
        <ModalFooter>
          <Group position="right">
            <Button type="button" onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={name.trim().length === 0 || sketcherValue == null}
              onClick={async () => {
                await withLoadingScreen(onAddNew(name, code, sketcherValue!));
                notifications.success(`${capitalize(counterpartName)} "${name}" created successfully`);
                onClose();
              }}
            >
              Create
            </Button>
          </Group>
        </ModalFooter>
      </Flex>
    </Modal>
  );
}

// Keep in sync with client/chemistry/molecules/tabs/MoleculePhysicochemicalProperties.tsx
const MOLECULAR_WEIGHT_PRECISION = 3;
const ROW_HEIGHT = 80;
const SIDE_PADDING = 40;
const SIDE_PADDING_PX = `${SIDE_PADDING}px`;

const COLUMNS: Array<ScrollableTableColumn> = [
  {header: 'Name', accessorKey: 'name', flexGrow: 1, isCollapsible: false, isSortable: true, sortKey: 'name'},
  {header: 'Code', accessorKey: 'code', isCollapsible: true, isSortable: true, sortKey: 'code'},
  {
    header: 'Chemical Structure',
    accessorKey: 'chemicalStructure',
    flexGrow: 2,
    isCollapsible: true,
    isSortable: false,
  },
  {
    header: 'Molecular Weight',
    accessorKey: 'molecularWeight',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'molecularWeight',
  },
  {
    header: 'Canonical SMILES',
    accessorKey: 'canonicalSmiles',
    flexGrow: 2,
    isCollapsible: true,
    isSortable: false,
    wrapText: true,
  },
  {header: 'ID', accessorKey: 'id', isCollapsible: true, isSortable: false, sortKey: 'id', wrapText: true},
  {header: 'Actions', accessorKey: 'actions', isCollapsible: false, isSortable: false},
];

export interface ChemicalCounterpartLibraryProps {
  counterpartName: string;
  items: Array<ChemicalCounterpartItem>;
  loading: boolean;
  tableKey: string;
  onAddNew: (name: string, code: string, sketcherValue: string) => Promise<void>;
  onEdit: (id: string, name: string, code: string) => Promise<void>;
  onArchive: (itemIds: Array<string>, purpose: string, options?: ArchiveOptionsForModal) => Promise<void>;
  onUnarchive: (itemIds: Array<string>) => Promise<void>;
}

export default function ChemicalCounterpartLibrary({
  counterpartName,
  items,
  loading,
  tableKey,
  onAddNew,
  onEdit,
  onArchive,
  onUnarchive,
}: ChemicalCounterpartLibraryProps): JSX.Element {
  const isTenantAdmin = useStoreSelector(
    EntityStore,
    useCallback((store: typeof EntityStore) => store.currentUser()?.isTenantAdmin ?? false, [])
  );
  const [query, setQuery] = useState('');
  const [selectedArchivePurposes, setSelectedArchivePurposes] = useState<Array<string>>([
    NOT_ARCHIVED_PURPOSE_STRING,
  ]);
  const [openedCreateModal, {open: openCreateModal, close: closeCreateModal}] = useDisclosure(false);
  const [editingItem, setEditingItem] = useState<ChemicalCounterpartItem | null>(null);

  const filteredItems = items
    .filter(
      (item) =>
        (selectedArchivePurposes.includes(NOT_ARCHIVED_PURPOSE_STRING) && !item.archiveData.archived) ||
        (item.archiveData.archived &&
          item.archiveData.purpose != null &&
          selectedArchivePurposes.includes(item.archiveData.purpose))
    )
    .filter(
      (item) =>
        !query ||
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        (item?.code ?? '').toLowerCase().includes(query.toLowerCase())
    );

  const rows: Array<ChemicalCounterpartRow> = filteredItems.map((item) => ({
    id: item.id,
    extra: {calculatedMolecularWeight: item.calculatedMolecularWeight},
    data: {
      name: item.name,
      code: item.code ?? '',
      chemicalStructure: (
        <ChemicalStructureSVG
          chemicalStructureImage={item.imageData}
          className={css(styles.chemicalStructureContainer)}
          svgClassName={css(styles.chemicalStructureSvg)}
        />
      ),
      molecularWeight: item.calculatedMolecularWeight?.toFixed(MOLECULAR_WEIGHT_PRECISION),
      canonicalSmiles: item.canonicalSmiles,
      id: item.id,
      actions: (
        <ChemicalCounterpartActions
          id={item.id}
          name={item.name}
          item={item}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onEditClick={() => {
            setEditingItem(item);
          }}
          canEdit={isTenantAdmin}
          canArchive={isTenantAdmin}
        />
      ),
    },
  }));

  return (
    <>
      {openedCreateModal && (
        <CreateChemicalCounterpartModal
          counterpartName={counterpartName}
          onClose={closeCreateModal}
          onAddNew={onAddNew}
        />
      )}
      {editingItem != null && (
        <EditChemicalCounterpartModal
          counterpartName={counterpartName}
          item={editingItem}
          onClose={() => {
            setEditingItem(null);
          }}
          onEdit={onEdit}
        />
      )}
      <Stack h="100%" w="100%" mih={0} style={{overflow: 'hidden'}}>
        <Box mx={SIDE_PADDING_PX} style={{flex: 'none'}}>
          {/* The Mantine fontsize theme is really large, temporarily hard-code the size until it's updated. */}
          <Title order={1} style={{fontSize: '24px'}}>
            {`${capitalize(counterpartName)} library`}
          </Title>
          {loading ? (
            <Flex justify="center">
              <Loader />
            </Flex>
          ) : (
            <Flex justify="space-between" my={12}>
              <Group>
                <PlainSearchInput
                  clearType={ClearType.X}
                  onChange={(e: InputChangeEvent) => {
                    setQuery(e.target.value);
                  }}
                  onEscape={() => {
                    setQuery('');
                  }}
                  placeholder={`${capitalize(counterpartName)} name or code`}
                  value={query}
                />
                <ArchivePurposeDropdown
                  selectedPurposes={selectedArchivePurposes}
                  onChangePurposes={setSelectedArchivePurposes}
                  itemIds={items.map((item) => item.id)}
                />
              </Group>
              <Group>
                <Tooltip label={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
                  <ActionIcon
                    aria-label={EXPORT_AUDIT_LOG_DISPLAY_STRING}
                    variant="tertiary"
                    onClick={() => {
                      exportAuditLogs(items.map((item) => item.id));
                    }}
                  >
                    <BenchlingIcon icon="audit-trail" />
                  </ActionIcon>
                </Tooltip>
                <Button disabled={!isTenantAdmin} variant="primary" size="sm" onClick={openCreateModal}>
                  {`Add new ${counterpartName}`}
                </Button>
              </Group>
            </Flex>
          )}
        </Box>
        {!loading && (
          <SortableScrollableTable
            className={css(styles.table)}
            matchParentHeight={true}
            canResizeColumns={true}
            columns={COLUMNS}
            getRowContentHeight={() => ROW_HEIGHT}
            emptyTableText="No items found."
            getSortValue={(row: ChemicalCounterpartRow, sortKey: string) => {
              switch (sortKey) {
                case 'name':
                case 'code':
                  return row.data[sortKey];
                case 'molecularWeight':
                  return row.extra.calculatedMolecularWeight ?? Infinity;
                default:
                  return '';
              }
            }}
            initialRows={rows}
            initialSort={[{sortKey: 'name'}]}
            tableKey={tableKey}
            calcLeftPadding={() => SIDE_PADDING}
          />
        )}
      </Stack>
    </>
  );
}

const styles = StyleSheet.create({
  table: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
  chemicalStructureContainer: {
    height: `${ROW_HEIGHT}px`,
    textAlign: 'center',
  },
  chemicalStructureSvg: {
    width: '100%',
    height: '100%',
  },
});
